<script setup lang="ts">
/**
 * Full-screen mobile overlay for a booking type detail view.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import {
  deleteBookingType,
  isBookingTypeHasBookingsError,
  type BookingType,
} from '@/api/bookingTypes'
import { useAddBookingTypeFormStore } from '@/stores/add_booking_type_form_store'
import { useAllBookingTypes } from '@/composables/useAllBookingTypes'
import DeleteConfirmSheet from '@/components/main/DeleteConfirmSheet.vue'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'

const props = defineProps<{
  bookingTypeId: string
}>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { hasRole } = usePermissions()
const canEdit = computed(() => hasRole(ResourceAccessRole.BookingTypesEdit))
const canDelete = computed(() => hasRole(ResourceAccessRole.BookingTypesDelete))

const { bookingTypes, isError: typesIsError, isDraining } = useAllBookingTypes()

const bookingType = computed<BookingType | null>(
  () => bookingTypes.value.find((item) => item.id === props.bookingTypeId) ?? null,
)

// ── Edit ──────────────────────────────────────────────────────────────────────
const addBookingTypeFormStore = useAddBookingTypeFormStore()

function openEdit(): void {
  const bt = bookingType.value
  if (!bt) return
  addBookingTypeFormStore.openForEdit(bt.id, {
    name: bt.name,
    description: bt.description,
    address: bt.address,
  })
}

// ── Delete ────────────────────────────────────────────────────────────────────
const queryClient = useQueryClient()

const showDeleteConfirm = ref(false)

const deleteMutation = useMutation({
  mutationFn: () => deleteBookingType(props.bookingTypeId),
  onSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['bookingTypes'] })
    void queryClient.invalidateQueries({ queryKey: ['bookingSchedule'] })
    showDeleteConfirm.value = false
    emit('close')
  },
})

const deleteError = computed<string | null>(() => {
  const error = deleteMutation.error.value
  if (!error) return null
  return isBookingTypeHasBookingsError(error)
    ? t('cards.bookingType.deleteHasBookings')
    : t('cards.bookingType.deleteFailed')
})

const deleteDescription = computed<string>(() => {
  const name = bookingType.value?.name ?? t('cards.bookingType.defaultTypeName')
  return t('cards.bookingType.confirmDeleteDescription', { name })
})

function handleDelete(): void {
  deleteMutation.reset()
  if (!bookingType.value) return
  showDeleteConfirm.value = true
}

function confirmDelete(): void {
  deleteMutation.mutate()
}

function cancelDelete(): void {
  showDeleteConfirm.value = false
}
</script>

<template>
  <Teleport to="body">
    <div class="btm-screen font-tufte text-tufte-text">
      <!-- iOS navbar -->
      <nav class="btm-navbar">
        <button type="button" class="btm-nav-back" @click="emit('close')">
          <svg
            width="8"
            height="14"
            viewBox="0 0 8 14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 1L1 7L7 13" />
          </svg>
          {{ t('tabs.booking.typesTab') }}
        </button>
        <span class="btm-nav-title">{{ t('cards.bookingType.navTitle') }}</span>
        <button
          v-if="canEdit"
          type="button"
          class="btm-nav-edit"
          :disabled="!bookingType"
          @click="openEdit"
        >
          {{ t('common.edit') }}
        </button>
      </nav>

      <!-- Loading / error -->
      <div v-if="!bookingType && isDraining" class="btm-state">
        {{ t('common.loadingShort') }}
      </div>
      <div v-else-if="!bookingType && typesIsError" class="btm-state btm-state--error">
        {{ t('cards.bookingType.loadError') }}
      </div>

      <!-- Content -->
      <div v-else-if="bookingType" class="btm-scroll">
        <div class="btm-header">
          <div class="btm-name">
            {{ bookingType.name ?? t('cards.bookingType.defaultTypeName') }}
          </div>
          <div v-if="bookingType.address" class="btm-address">{{ bookingType.address }}</div>
        </div>

        <div class="btm-section-label">{{ t('forms.addBookingType.description') }}</div>
        <div class="btm-group">
          <div v-if="bookingType.description" class="btm-description">
            {{ bookingType.description }}
          </div>
          <div v-else class="btm-description btm-description--empty">
            {{ t('cards.bookingType.noDescription') }}
          </div>
        </div>

        <!-- Grows to push Delete to the bottom of the screen when content is
             short; shrinks to 0 and lets the normal scroll take over once
             content overflows the viewport. -->
        <div class="btm-fill" />

        <div v-if="canDelete" class="btm-danger-group">
          <button
            type="button"
            class="btm-danger-row"
            :disabled="deleteMutation.isPending.value"
            @click="handleDelete"
          >
            {{ deleteMutation.isPending.value ? t('common.deleting') : t('common.delete') }}
          </button>
        </div>

        <div class="btm-scroll-end" />
      </div>

      <DeleteConfirmSheet
        :open="showDeleteConfirm"
        :title="t('cards.bookingType.confirmDeleteTitle')"
        :description="deleteDescription"
        :pending="deleteMutation.isPending.value"
        :error="deleteError"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </div>
  </Teleport>
</template>

<style scoped>
.btm-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: #f4f3ea;
  display: flex;
  flex-direction: column;
}

/* ── Navbar ──────────────────────────────────────────────────────────────── */
.btm-navbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem 0.5rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.btm-nav-back {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  justify-content: flex-start;
}
.btm-nav-back:active {
  opacity: 0.6;
}

.btm-nav-title {
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  flex: 1;
  text-align: center;
}

.btm-nav-edit {
  font: inherit;
  font-size: 0.9rem;
  text-transform: uppercase;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  text-align: right;
}
.btm-nav-edit:disabled {
  color: var(--color-tufte-muted);
  cursor: default;
}
.btm-nav-edit:not(:disabled):active {
  opacity: 0.6;
}

/* ── Loading / error ─────────────────────────────────────────────────────── */
.btm-state {
  padding: 1.5rem 1.25rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.9rem;
  color: var(--color-tufte-muted);
}
.btm-state--error {
  color: var(--color-money-negative);
}

/* ── Scrollable content ──────────────────────────────────────────────────── */
.btm-scroll {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* ── Header ──────────────────────────────────────────────────────────────── */
.btm-header {
  background: var(--color-tufte-bg);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 1.3rem 1.25rem 1.2rem;
}

.btm-name {
  font-size: 1.9rem;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.01em;
}

.btm-address {
  font-family: var(--font-tufte-sans);
  font-size: 0.85rem;
  color: var(--color-tufte-muted);
  margin-top: 0.5rem;
}

/* ── Section label ───────────────────────────────────────────────────────── */
.btm-section-label {
  font-family: var(--font-tufte-sans);
  font-size: 0.7rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  padding: 0 1.25rem 0.4rem;
  margin-top: 1.2rem;
}

/* ── Group card ──────────────────────────────────────────────────────────── */
.btm-group {
  background: var(--color-tufte-bg);
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
}

.btm-description {
  padding: 0.85rem 1.25rem;
  font-size: 1.02rem;
  line-height: 1.5;
  color: var(--color-tufte-secondary);
}
.btm-description--empty {
  font-style: italic;
  color: var(--color-tufte-muted);
  opacity: 0.6;
}

.btm-fill {
  flex: 1;
}

/* ── Danger group ────────────────────────────────────────────────────────── */
.btm-danger-group {
  margin-top: 1.4rem;
}

.btm-danger-row {
  width: 100%;
  display: block;
  background: var(--color-tufte-bg);
  border: none;
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 0.85rem 1.25rem;
  text-align: center;
  font: inherit;
  font-size: 0.95rem;
  text-transform: uppercase;
  color: var(--color-tufte-danger);
  cursor: pointer;
}
.btm-danger-row:active {
  background: color-mix(in srgb, var(--color-tufte-danger) 5%, var(--color-tufte-bg));
}
.btm-danger-row:disabled {
  opacity: 0.5;
  cursor: default;
}

.btm-scroll-end {
  height: 2.5rem;
}
</style>
